// src/admin/Dashboard.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";

const getAdminStatsFn = httpsCallable(functions, "getAdminStats");

const S = `
/* ── Stat cards ── */
.dash-stats {
  display:grid;
  grid-template-columns:repeat(3,1fr);
  gap:12px; margin-bottom:24px;
}
.stat-card {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; padding:18px 18px 16px;
  border-top:2px solid transparent;
  transition:border-color 0.15s;
}
.stat-card:hover { border-color:rgba(255,255,255,0.12); }
.stat-card-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:10px; }
.stat-card-label { font-size:10px; font-weight:700; color:#6b6b6b; letter-spacing:0.8px; text-transform:uppercase; }
.stat-card-icon { font-size:18px; opacity:0.8; }
.stat-card-value { font-size:24px; font-weight:800; color:#fff; letter-spacing:-0.5px; margin-bottom:4px; line-height:1; }
.stat-card-sub { font-size:11px; color:#6b6b6b; font-weight:500; }

/* ── Section title ── */
.dash-sect-title {
  font-size:13px; font-weight:700; color:#fff;
  margin-bottom:12px;
  display:flex; align-items:center; justify-content:space-between;
}
.dash-sect-title a { font-size:11px; font-weight:600; color:#0078F2; text-decoration:none; }
.dash-sect-title a:hover { color:#0a84ff; }

/* ── Low stock ── */
.low-stock-card {
  background:#1a1a1a; border:1px solid rgba(234,179,8,0.18);
  border-radius:8px; padding:13px 16px; margin-bottom:8px;
  display:flex; align-items:center; justify-content:space-between; gap:12px;
}
.low-stock-add {
  font-size:11px; font-weight:700; color:#eab308;
  background:rgba(234,179,8,0.08); border:1px solid rgba(234,179,8,0.2);
  border-radius:6px; padding:6px 14px; text-decoration:none; flex-shrink:0;
  transition:background 0.15s; white-space:nowrap;
}
.low-stock-add:hover { background:rgba(234,179,8,0.14); }

/* ── Orders table — desktop ── */
.dash-table {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08);
  border-radius:10px; overflow:hidden; margin-bottom:24px;
}
.dash-table-head {
  display:grid; grid-template-columns:2fr 1fr 1fr 1fr;
  gap:12px; padding:11px 18px;
  border-bottom:1px solid rgba(255,255,255,0.06);
}
.dash-table-head-cell { font-size:10px; font-weight:700; color:#6b6b6b; text-transform:uppercase; letter-spacing:1px; }
.dash-table-row {
  display:grid; grid-template-columns:2fr 1fr 1fr 1fr;
  gap:12px; padding:13px 18px;
  border-bottom:1px solid rgba(255,255,255,0.04);
  font-size:13px; color:#a3a3a3; align-items:center; transition:background 0.12s;
}
.dash-table-row:last-child { border-bottom:none; }
.dash-table-row:hover { background:rgba(255,255,255,0.02); }

/* ── Orders card — mobile ── */
.dash-order-card {
  background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.06);
  border-radius:8px; padding:13px 14px; margin-bottom:8px;
  display:none;
}
.status-pill {
  display:inline-block; padding:3px 10px; border-radius:4px;
  font-size:10px; font-weight:700; letter-spacing:0.5px;
}

.skel { background:#222; border-radius:8px; animation:skPulse 1.5s ease infinite; }
@keyframes skPulse { 0%,100%{opacity:.5} 50%{opacity:1} }

/* ════════════════════
   MOBILE ≤ 768px
════════════════════ */
@media(max-width:768px) {
  .dash-stats { grid-template-columns:repeat(2,1fr); gap:10px; margin-bottom:18px; }
  .stat-card { padding:14px 14px 12px; }
  .stat-card-value { font-size:20px; }

  /* Hide desktop table, show mobile cards */
  .dash-table { display:none; }
  .dash-order-card { display:block; }
}

@media(max-width:380px) {
  .dash-stats { grid-template-columns:1fr 1fr; }
}
`;

const STATUS_COLORS = {
  delivered: { bg:"rgba(34,197,94,0.1)",   border:"rgba(34,197,94,0.2)",   color:"#22c55e" },
  pending:   { bg:"rgba(0,120,242,0.1)",    border:"rgba(0,120,242,0.2)",   color:"#0078F2" },
  paid:      { bg:"rgba(234,179,8,0.1)",    border:"rgba(234,179,8,0.2)",   color:"#eab308" },
  expired:   { bg:"rgba(255,255,255,0.04)", border:"rgba(255,255,255,0.08)",color:"#6b6b6b" },
  failed:    { bg:"rgba(239,68,68,0.08)",   border:"rgba(239,68,68,0.15)",  color:"#ef4444" },
};

function StatCard({ icon, label, value, sub, accentColor="#0078F2" }) {
  return (
    <div className="stat-card" style={{ borderTopColor:accentColor }}>
      <div className="stat-card-top">
        <div className="stat-card-label">{label}</div>
        <div className="stat-card-icon">{icon}</div>
      </div>
      <div className="stat-card-value">{value}</div>
      {sub && <div className="stat-card-sub">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  function load() {
    return getAdminStatsFn()
      .then(res => { setStats(res.data); })
      .catch(() => {});
  }

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  async function handleRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  if (loading) return (
    <>
      <style>{S}</style>
      <div className="dash-stats">
        {[...Array(6)].map((_,i) => <div key={i} className="skel" style={{ height:96 }} />)}
      </div>
      <div className="skel" style={{ height:280 }} />
    </>
  );

  if (!stats) return (
    <>
      <style>{S}</style>
      <div style={{ textAlign:"center", padding:"60px 0", color:"#6b6b6b", fontSize:14 }}>
        Failed to load stats. Check your Cloud Functions.
      </div>
    </>
  );

  const recentOrders = (stats.recentOrders || []).slice(0, 8);
  const noStockOrders = recentOrders.filter(o => o.status === "paid_no_stock");

  return (
    <>
      <style>{S}</style>

      {/* Top bar with refresh */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ fontSize:16, fontWeight:800, color:"#fff" }}>📊 Dashboard</div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            display:"flex", alignItems:"center", gap:6,
            background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)",
            borderRadius:6, color:"#a3a3a3", fontFamily:"'Inter',sans-serif",
            fontSize:12, fontWeight:600, padding:"7px 14px", cursor:"pointer",
            transition:"all 0.15s", opacity: refreshing ? 0.5 : 1,
          }}
        >
          <span style={{ display:"inline-block", animation: refreshing ? "spin 0.8s linear infinite" : "none" }}>↻</span>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>

      {/* Paid but no stock alert */}
      {noStockOrders.length > 0 && (
        <div style={{
          background:"rgba(249,115,22,0.08)", border:"1px solid rgba(249,115,22,0.25)",
          borderRadius:8, padding:"12px 16px", marginBottom:16,
          display:"flex", alignItems:"center", gap:12,
        }}>
          <span style={{ fontSize:18 }}>⚠️</span>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:"#f97316", marginBottom:2 }}>
              {noStockOrders.length} order{noStockOrders.length > 1 ? "s" : ""} paid but no stock
            </div>
            <div style={{ fontSize:12, color:"#6b6b6b" }}>
              Go to Orders → filter by "No Stock" to manually deliver credentials.
            </div>
          </div>
          <Link to="/admin/orders" style={{ marginLeft:"auto", fontSize:12, fontWeight:700, color:"#f97316", textDecoration:"none", flexShrink:0 }}>
            Fix Now →
          </Link>
        </div>
      )}

      {/* Stat cards */}
      <div className="dash-stats">
        <StatCard icon="💰" label="Today"      value={`$${stats.revenue.today.toFixed(2)}`}     sub="Revenue today"     accentColor="#22c55e" />
        <StatCard icon="📅" label="This Month" value={`$${stats.revenue.thisMonth.toFixed(2)}`} sub="Month to date"     accentColor="#0078F2" />
        <StatCard icon="🏆" label="All Time"   value={`$${stats.revenue.allTime.toFixed(2)}`}   sub="Total revenue"     accentColor="#0078F2" />
        <StatCard icon="📦" label="Orders"     value={stats.orders.total}                       sub={`${stats.orders.pending} pending`} accentColor="#eab308" />
        <StatCard icon="👥" label="Customers"  value={stats.customers.total}                    sub="Registered"        accentColor="#22c55e" />
        <StatCard icon="🎮" label="Games"      value={stats.games.total}                        sub={`${stats.games.lowStock?.length||0} low stock`} accentColor="#f97316" />
      </div>

      {/* Low stock alert */}
      {stats.games.lowStock?.length > 0 && (
        <div style={{ marginBottom:22 }}>
          <div className="dash-sect-title">
            ⚠️ Low Stock Alert
            <Link to="/admin/stock">Manage Stock →</Link>
          </div>
          {stats.games.lowStock.map(g => (
            <div key={g.id} className="low-stock-card">
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:2 }}>{g.title}</div>
                <div style={{ fontSize:11, color:"#6b6b6b" }}>{g.stock === 0 ? "Out of stock!" : `Only ${g.stock} left`}</div>
              </div>
              <Link to={`/admin/stock?game=${g.id}`} className="low-stock-add">Add Stock</Link>
            </div>
          ))}
        </div>
      )}

      {/* Recent orders */}
      <div className="dash-sect-title">
        Recent Orders
        <Link to="/admin/orders">View All →</Link>
      </div>

      {/* Desktop table */}
      <div className="dash-table">
        <div className="dash-table-head">
          <div className="dash-table-head-cell">Game</div>
          <div className="dash-table-head-cell">Amount</div>
          <div className="dash-table-head-cell">Status</div>
          <div className="dash-table-head-cell">Date</div>
        </div>
        {recentOrders.map(o => {
          const cfg = STATUS_COLORS[o.status] || STATUS_COLORS.pending;
          return (
            <div key={o.orderId} className="dash-table-row">
              <div>
                <div style={{ color:"#fff", fontWeight:600, marginBottom:2 }}>{o.gameTitle}</div>
                <div style={{ fontSize:11, color:"#6b6b6b" }}>{o.userEmail}</div>
              </div>
              <div style={{ fontWeight:700, color:"#fff" }}>${o.amount?.toFixed(2)}</div>
              <div>
                <span className="status-pill" style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, color:cfg.color }}>
                  {o.status}
                </span>
              </div>
              <div style={{ color:"#6b6b6b" }}>
                {o.createdAt ? new Date(o.createdAt).toLocaleDateString("en-US",{day:"numeric",month:"short"}) : "—"}
              </div>
            </div>
          );
        })}
        {recentOrders.length === 0 && (
          <div style={{ padding:"40px", textAlign:"center", color:"#6b6b6b", fontSize:13 }}>No orders yet</div>
        )}
      </div>

      {/* Mobile cards */}
      {recentOrders.map(o => {
        const cfg = STATUS_COLORS[o.status] || STATUS_COLORS.pending;
        return (
          <div key={o.orderId} className="dash-order-card">
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10, marginBottom:8 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:2 }}>{o.gameTitle}</div>
                <div style={{ fontSize:11, color:"#6b6b6b" }}>{o.userEmail}</div>
              </div>
              <div style={{ fontSize:15, fontWeight:800, color:"#fff", flexShrink:0 }}>${o.amount?.toFixed(2)}</div>
            </div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span className="status-pill" style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, color:cfg.color }}>
                {o.status}
              </span>
              <span style={{ fontSize:11, color:"#6b6b6b" }}>
                {o.createdAt ? new Date(o.createdAt).toLocaleDateString("en-US",{day:"numeric",month:"short"}) : "—"}
              </span>
            </div>
          </div>
        );
      })}
      {recentOrders.length === 0 && (
        <div className="dash-order-card" style={{ textAlign:"center", color:"#6b6b6b", fontSize:13 }}>No orders yet</div>
      )}
    </>
  );
}